/**
 * The deep link from a member's detail drawer into that member's wallet page.
 *
 * A member drawer answers "who"; the question that follows a deposit or a
 * withdrawal is "did it land", and the answer is the member's own ledger, which
 * already lives on `/wallet`. Rather than grow a second ledger inside the
 * drawer, the drawer links to it — so there is one wallet screen, and the
 * member it lands on is the one the operator can see and change in its picker.
 *
 * The link is the whole contract between the two screens: `WalletPage` seeds
 * its selection from it, so both halves are tested here against the same shape.
 */

/**
 * What `member.members.id` actually is — `cols.id()`, a UUID. The value is read
 * out of the query string and interpolated into a request path, so nothing
 * looser will do: a character class admitting dots would admit `..` and with it
 * a hand-edited `?memberId=../../admin`. Anything that is not a UUID is read as
 * "no member" and the page opens on its picker, exactly as an unlinked visit
 * does.
 */
const MEMBER_ID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export function walletMemberPath(memberId: string): string {
  return `/wallet?${new URLSearchParams({ memberId }).toString()}`;
}

/** The member carried by a `/wallet` URL, or `''`. */
export function readWalletMemberId(params: URLSearchParams): string {
  const raw = (params.get('memberId') ?? '').trim();
  return MEMBER_ID.test(raw) ? raw : '';
}

/**
 * The query keys this link owns. `WalletPage` clears them once it has read
 * them, so the URL never keeps naming a member the operator has since swapped
 * out in the picker — and a reload cannot silently swap it back.
 */
export const WALLET_LINK_PARAMS: readonly string[] = ['memberId'];
